import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import AgentSidebar from '../components/AgentSidebar';
import CountrySelect from '../components/CountrySelect';

const API_BASE = process.env.REACT_APP_API_BASE_URL || `${process.env.FRONTEND_URL}/api`;

const STATUS_STYLES = {
  PROCESSING: 'bg-amber-50 text-amber-700',
  COMPLETED: 'bg-green-100 text-green-700',
  FAILED: 'bg-red-100 text-red-700',
};

export default function AgentCourseImportPage() {
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [country, setCountry] = useState('');
  const [file, setFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);

  const token = localStorage.getItem('token');

  const fetchBatches = () => {
    setLoading(true);
    fetch(`${API_BASE}/admin/courses/imports`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json())
      .then((json) => setBatches(json.success ? json.data : []))
      .finally(() => setLoading(false));
  };

  useEffect(fetchBatches, []);

  useEffect(() => {
    const hasProcessing = batches.some((b) => b.status === 'PROCESSING');
    if (!hasProcessing) return;
    const interval = setInterval(fetchBatches, 5000);
    return () => clearInterval(interval);
  }, [batches]);

  const handleUpload = async () => {
    if (!country || !file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('country', country);
    fd.append('document', file);
    await fetch(`${API_BASE}/admin/courses/imports`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: fd,
    });
    setUploading(false);
    setCountry('');
    setFile(null);
    fetchBatches();
  };

  return (
    <div className="bg-[#f7f9fb] font-['Plus_Jakarta_Sans'] min-h-screen">
      <AgentSidebar />
      <main className="pt-16 md:pt-2 md:ml-64 min-h-screen p-8">
        <div className="mb-10">
          <h2 className="text-3xl font-bold text-[#00255a] mb-2">Import Courses</h2>
          <p className="text-[#64748b]">Upload a country course document — AI extracts the data, an admin approves before it publishes.</p>
        </div>

        <div className="bg-white rounded-2xl border border-[#e2e8f0] shadow-sm p-8 mb-10">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
            <div>
              <label className="block text-xs font-bold text-[#44474f] uppercase mb-2">Country</label>
              <CountrySelect required value={country} onChange={setCountry} className="w-full px-4 py-3 rounded-xl border border-[#c4c6d1] outline-none focus:ring-2 focus:ring-[#00255a]/10" />
            </div>

            <div
              onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
              onDragLeave={() => setDragActive(false)}
              onDrop={(e) => { e.preventDefault(); setDragActive(false); setFile(e.dataTransfer.files[0]); }}
              onClick={() => document.getElementById('agent-course-doc-input').click()}
              className={`md:col-span-2 cursor-pointer border-2 border-dashed rounded-2xl p-8 text-center transition-all ${
                dragActive ? 'border-[#00255a] bg-[#00255a]/5' : 'border-[#e2e8f0] hover:border-[#00255a]'
              }`}
            >
              <input
                id="agent-course-doc-input"
                type="file"
                className="hidden"
                accept=".pdf"
                onChange={(e) => setFile(e.target.files[0])}
              />
              <span className="material-symbols-outlined text-4xl text-[#64748b] mb-2 block">upload_file</span>
              <p className="font-semibold text-[#0f172a]">
                {file ? file.name : 'Drag your course document here or click to browse'}
              </p>
              <p className="text-xs text-[#64748b] mt-1">PDF only — DOCX support is unreliable for AI extraction</p>
            </div>
          </div>

          <button
            onClick={handleUpload}
            disabled={!country || !file || uploading}
            className="mt-6 px-8 py-3 text-white font-semibold rounded-xl shadow-md disabled:opacity-50 hover:scale-[1.02] transition-all"
            style={{ background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 60%, #ff7b7b 100%)' }}
          >
            {uploading ? 'Uploading...' : 'Start Import'}
          </button>
        </div>

        <section className="bg-white rounded-2xl border border-[#e2e8f0] shadow-sm overflow-hidden">
          <div className="p-6 border-b border-[#e2e8f0]">
            <h3 className="font-bold text-[#00255a]">Import History</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-[#f2f4f6]">
                <tr>
                  {['Country', 'File Name', 'Uploaded By', 'Date', 'Status', 'Extracted', ''].map((h) => (
                    <th key={h} className="px-6 py-4 text-xs uppercase text-[#64748b]">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-[#e2e8f0]">
                {loading ? (
                  <tr><td colSpan="7" className="p-12 text-center text-[#64748b]">Loading...</td></tr>
                ) : batches.length === 0 ? (
                  <tr><td colSpan="7" className="p-12 text-center text-[#64748b]">No imports yet.</td></tr>
                ) : (
                  batches.map((b) => (
                    <tr key={b.id} className="hover:bg-[#f7f9fb] transition-colors">
                      <td className="px-6 py-4 font-semibold text-[#00255a]">{b.country}</td>
                      <td className="px-6 py-4 text-sm text-[#44474f]">{b.sourceFileName || '—'}</td>
                      <td className="px-6 py-4 text-sm text-[#64748b]">{b.uploadedBy?.fullName}</td>
                      <td className="px-6 py-4 text-sm text-[#64748b]">{new Date(b.createdAt).toLocaleDateString()}</td>
                      <td className="px-6 py-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-bold ${STATUS_STYLES[b.status]}`}>{b.status}</span>
                      </td>
                      <td className="px-6 py-4 text-sm text-[#44474f]">{b._count?.courses ?? b.extractedCount}</td>
                      <td className="px-6 py-4 text-right">
                        {b.status === 'COMPLETED' && (
                          <Link to={`/agent/courses/review?batch=${b.id}`} className="text-[#00255a] font-semibold text-sm hover:underline">
                            Review Extracted Courses
                          </Link>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>
      </main>
    </div>
  );
}
